// FILE: src/features/auditLogsSlice.js
// The admin audit log (WP-7, B-9, R4, matrix 5.8): the server filters and
// pages. GET /api/admin/audit-logs
//   ?userId ?email ?action (one or a comma list) ?outcome (SUCCESS|FAILURE|INFO)
//   ?from ?to (Toronto days YYYY-MM-DD, `to` included) ?page ?limit (≤ 200)
// → { logs, totalCount, page, limit, totalPages }, newest first; each log's
// userId is { _id, email, firstName, lastName, role } or null.
// `query` is the request the stored page answers, so a page that asks for
// something else (another student's activity) never shows these rows.
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import fetchWithAuth from "../utils/fetchWithAuth";

const KEYS = ["userId", "email", "action", "outcome", "from", "to", "page", "limit"];

/** The query string for a filter object (empty values left out, fixed order). */
export function auditQuery(filters = {}) {
  const params = new URLSearchParams();
  KEYS.forEach((k) => {
    const v = filters[k];
    if (v !== undefined && v !== null && v !== "") params.set(k, String(v));
  });
  return params.toString();
}

export const fetchAuditLogs = createAsyncThunk("auditLogs/fetchAuditLogs", async (filters = {}, { rejectWithValue }) => {
  try {
    return await fetchWithAuth(`/api/admin/audit-logs?${auditQuery(filters)}`);
  } catch (err) {
    return rejectWithValue(err?.body || err?.message || "INTERNAL_ERROR");
  }
});

const auditLogsSlice = createSlice({
  name: "auditLogs",
  initialState: {
    logs: [],
    totalCount: 0,
    page: 1,
    limit: 25,
    totalPages: 1,
    query: null,
    loading: false,
    error: null,
    requestId: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchAuditLogs.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchAuditLogs.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return; // a newer request is on its way
        const p = action.payload;
        const filters = action.meta.arg || {};
        state.loading = false;
        state.query = auditQuery(filters);
        if (Array.isArray(p)) {
          // Servers before WP-7 answered a bare array.
          state.logs = p;
          state.totalCount = p.length;
          state.page = 1;
          state.limit = Number(filters.limit) || p.length || 25;
          state.totalPages = 1;
          return;
        }
        state.logs = Array.isArray(p?.logs) ? p.logs : [];
        state.totalCount = Number(p?.totalCount) || 0;
        state.page = Number(p?.page) || 1;
        state.limit = Number(p?.limit) || Number(filters.limit) || 25;
        state.totalPages = Number(p?.totalPages) || 1;
      })
      .addCase(fetchAuditLogs.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.error = action.payload || action.error?.message || "INTERNAL_ERROR";
        const query = auditQuery(action.meta.arg || {});
        if (query !== state.query) {
          // Rows answering another request must not show as this one's.
          state.logs = [];
          state.totalCount = 0;
          state.query = query;
        }
      });
  }
});

export default auditLogsSlice.reducer;
